import type { Query } from '@w/db'
import { queryOptions } from '@w/react-query/db'
import { SchemaError, type Schema } from '@w/schema'
import { safe } from '@w/utils'
import type { Tel } from '@w/tel'
import type { ItemReadResource } from './entries'
import { isPageKind } from '../server/db/page-kind-policy'
import { languageSchema } from '../lexicon/lexicon-model'

/** SQLite enum decoding is part of the card schema boundary. SQL errors occur before it. */
export function isCardDataError(error: unknown) {
  return error instanceof SchemaError || error instanceof Error && /^(Enum column expects number \(code\), got |Enum code [^ ]+ is not mapped for values: )/.test(error.message)
}

/** Product decode boundaries a reader can report. */
export type ItemFailureCode = 'PAGE_DOCUMENT' | 'PAGE_METADATA' | 'REMOVED_CONTENT' | 'TRANSLATION_DATA' | 'CARD_DATA'

/** A row the query returned but the product cannot decode (Spark PERMISSIVE). */
export type Unreadable = { unreadable: ItemFailureCode; key: string; pageId?: string }
export function isUnreadable(value: unknown): value is Unreadable {
  return typeof value === 'object' && value !== null && 'unreadable' in value
}
export function readable<T>(values: readonly (T | Unreadable)[]): T[] {
  return values.filter((value): value is T => !isUnreadable(value))
}

const pageDataError = /^(Invalid page (block|document|link)|Invalid question (block|options)|Structural page references must be local|Invalid structural page reference)/

/** A read runs its query and decodes each row; a declared data error becomes a placeholder. Writes nothing. */
export function itemRowsOptions<T>(resource: ItemReadResource, query: Query, schema: Schema<T>, kind: 'page' | 'card', code: ItemFailureCode) {
  const options = queryOptions(query, schema)
  const { sql, params } = query.toSql()
  return { ...options, queryKey: [...options.queryKey, resource.userId, resource.connectionId, 'contained'],
    queryFn: async (qctx?: { tel?: Tel }): Promise<(T | Unreadable)[]> => {
      const rows = await safe(resource.db.driver).query<Record<string, unknown>>(sql, params, qctx)
      return rows.map(row => {
        try {
          const value = schema.parse(row)
          if (kind === 'page' && code === 'PAGE_METADATA' && row.kind === 'language') languageSchema.parse(row.language)
          return value
        }
        catch (error) {
          if (!(error instanceof SchemaError) && !(kind === 'card' && isCardDataError(error)) && !(kind === 'page' && error instanceof Error && pageDataError.test(error.message))) throw error
          if (kind === 'card') return { unreadable: code, key: String(row.qid), pageId: String(row.pageId) }
          return { unreadable: isPageKind(row.kind) ? code : 'PAGE_METADATA', key: String(row.id) }
        }
      })
    },
  }
}

/** Readers that show only healthy rows omit placeholders (Spark DROPMALFORMED). */
export function readableRowsOptions<T>(resource: ItemReadResource, query: Query, schema: Schema<T>, kind: 'page' | 'card', code: ItemFailureCode) {
  const options = itemRowsOptions(resource, query, schema, kind, code)
  return { ...options, queryFn: async (qctx?: { tel?: Tel }): Promise<T[]> => readable(await options.queryFn(qctx)) }
}

/** Decode rows, omitting each one that fails its schema; any other error propagates. */
export function decodeReadable<T>(rows: readonly unknown[], schema: Schema<T>): T[] {
  return rows.flatMap(row => {
    try { return [schema.parse(row)] }
    catch (error) {
      if (!(error instanceof SchemaError)) throw error
      return []
    }
  })
}

// Fixed labels only; never echo the decode error or row content.
const unreadableReasons: Record<ItemFailureCode, string> = {
  PAGE_DOCUMENT: 'Page content unavailable', PAGE_METADATA: 'Page details unavailable',
  REMOVED_CONTENT: 'Removed content unavailable', TRANSLATION_DATA: 'Translation unavailable', CARD_DATA: 'Card unavailable',
}
export function unreadableReason(code: ItemFailureCode): string {
  return unreadableReasons[code]
}
